<template>
	<div tabindex="0" class="hide-outline" style="margin-top: 58px">
		<ui-row>
			<ui-col cols="12">
				<ui-card @submit="onSubmit">
					<ui-title class="has-text-white mb-2">{{ $t('license.title') }}</ui-title>
					<ui-progress v-if="isPendingRootGet"/>
					<div v-else>
						<ui-alert class="my-3" v-if="hasNoLicense" error>
							{{ $t('license.no_license') }}
						</ui-alert>
						<ui-row>
							<ui-col cols="6">
								<div class="License-Item">
									<div>
										<div class="License-Title"> {{ $t('license.active_title') }}</div>
										<div class="License-Description">
											{{ $t('license.active_description') }}
										</div>
									</div>
									<div class="is-flex is-align-items-center">
										<ui-switch v-model="formData.active"/>
									</div>
								</div>
							</ui-col>

							<ui-col cols="6">
								<div class="License-Item">
									<div>
										<div class="License-Title">{{ $t('license.users_title') }}</div>
										<div class="License-Description">
											{{ $t('license.users_description') }}
										</div>
									</div>
									<div class="is-flex is-align-items-center">
										<ui-text-field v-model.number="formData.users" :rules="[isInteger]" type="number"/>
									</div>
								</div>
							</ui-col>

							<ui-col cols="6">
								<div class="License-Item">
									<div>
										<div class="License-Title">{{ $t('license.api_title') }}</div>
										<div class="License-Description">
											{{ $t('license.api_description') }}
										</div>
									</div>
									<div class="is-flex is-align-items-center">
										<ui-switch v-model="formData.apiKey"/>
									</div>
								</div>
							</ui-col>

							<ui-col cols="6">
								<div class="License-Item">
									<div>
										<div class="License-Title">{{ $t('license.buildings_title') }}</div>
										<div class="License-Description">
											{{ $t('license.buildings_description') }}
										</div>
									</div>
									<div class="is-flex is-align-items-center">
										<ui-text-field v-model.number="formData.buildings" :rules="[isInteger]" type="number"/>
									</div>
								</div>
							</ui-col>

							<ui-col cols="6">
								<div class="License-Item">
									<div>
										<div class="License-Title">{{ $t('license.checklist_title') }}</div>
										<div class="License-Description">
											{{ $t('license.checklist_description') }}
										</div>
									</div>
									<div class="is-flex is-align-items-center">
										<ui-switch v-model="formData.checklist"/>
									</div>
								</div>
							</ui-col>
						</ui-row>
					</div>


					<template #actions>
						<div class="is-flex is-justify-content-space-between">
							<div class="is-flex is-align-items-center">
								<ui-alert v-if="error" error>{{ error }}</ui-alert>
								<ui-alert v-if="successMessage" success>{{ successMessage }}</ui-alert>
							</div>
							<ui-btn class="is-info" :loading="isPending" type="submit" icon="fa-save" :disabled="!rootCustomer">{{ hasNoLicense ? $t('license.create') : $t('license.save') }}</ui-btn>
						</div>
					</template>
				</ui-card>
			</ui-col>
		</ui-row>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

const initialFormData = () => ({
	active: true,
	users: 0,
	buildings: 0,
	checklist: true,
	apiKey: false
});

export default {
	data: () => ({
		formData: initialFormData(),
		rootCustomer: null,
		isPendingRootGet: false,
		isPending: false,
		error: '',
		successMessage: ''
	}),

	computed: {
		...mapGetters('user', ['getActiveCustomer', 'getActiveCompany']),

		isEdit() {
			return Boolean(this.rootCustomer?.license?.id);
		},

		hasNoLicense() {
			return !this.rootCustomer?.license?.id;
		}
	},

	watch: {
		'getActiveCustomer.id'() {
			this.error = '';
			this.successMessage = '';
			this.getRootCustomer();
		}
	},

	created() {
		this.getRootCustomer();
	},

	methods: {
		...mapActions('user', ['setLicense']),

		mapFormData() {
			if (this.isEdit) {
				// eslint-disable-next-line no-unused-vars
				const { id, customerId, ...licenseObj } = this.rootCustomer.license;
				this.formData = {
					...this.formData,
					...licenseObj
				};

				return;
			}

			this.formData = initialFormData();
		},

		isInteger(value) {
			return !Number.isInteger(value) ? this.$t('license.validation_integer') : null;
		},

		onSubmit() {
			this.error = '';
			this.successMessage = '';

			if (this.isEdit) {
				this.updateLicense();

				return;
			}

			this.createLicense();
		},

		async getRootCustomer() {
			try {
				this.rootCustomer = await this.$services.customer.getRootCustomer({
					customerId: this.getActiveCustomer.id,
					companyId: this.getActiveCompany.id
				}, {
					throwException: true,
					loading: val => {
						this.isPendingRootGet = val;
					}
				});

				this.mapFormData();
			} catch (error) {
				this.error = error.message;
			}
		},

		async updateLicense() {
			try {
				const data = await this.$services.customerLicense.update(this.rootCustomer.license.id, {
					...this.formData,
					customerId: this.rootCustomer.id
				}, {
					throwException: true,
					loading: val => {
						this.isPending = val;
					}
				});

				this.setLicense(data);

				this.successMessage = this.$t('license.updated');
			} catch (error) {
				this.error = error.message;
			}
		},

		async createLicense() {
			try {
				const data = await this.$services.customerLicense.create({
					...this.formData,
					customerId: this.rootCustomer.id
				}, {
					throwException: true,
					loading: val => {
						this.isPending = val;
					}
				});

				this.setLicense(data);
				this.getRootCustomer();

				this.successMessage = this.$t('license.created');
			} catch (error) {
				this.error = error.message;
			}
		}
	}
};
</script>

<style lang="scss">
	.License-Item {
		display: flex;
		justify-content: space-between;
		padding: 10px;
		border-radius: 4px;
		background-color: lighten(#363a41, 10%);
		box-shadow: rgba(0, 0, 0, 0.25) 0px 0.0625em 0.0625em, rgba(0, 0, 0, 0.25) 0px 0.125em 0.5em, rgba(255, 255, 255, 0.1) 0px 0px 0px 1px inset;
	}

	.License-Title {
		font-weight: bold;
	}

	.License-Description {
		font-style: italic;
		font-size: 12px;
	}
</style>
